import classNames from 'classnames'
import { usePathname, useSearchParams } from 'next/navigation'
import React, {
  CSSProperties,
  forwardRef,
  HTMLAttributeAnchorTarget,
  memo,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react'

import PAGE_EXPERIMENTS_CONFIG_LIST from '@src/experiments/pageExperiments/configs/pageExperiments.configList'
import { getAssignedBucketForPageExperiment } from '@src/experiments/pageExperiments/pageExperiment.state'
import { getSlugForBucket } from '@src/experiments/pageExperiments/pageExperiments.utils'
import { Link } from '@src/modules/navigation'

import withTracking from '../../hocs/withTracking'

import styles from './link.module.scss'

const HEX_COLOR_CODE_REGEX = /^#(?:[0-9a-fA-F]{3}){1,2}$/

export interface LinkProps {
  children: ReactNode
  onClick?: (...args: any[]) => any
  href?: string | undefined
  className?: string
  disabled?: boolean
  target?: HTMLAttributeAnchorTarget
  withoutBaseStyle?: boolean
  enableEarlyFetch?: boolean
  style?: CSSProperties
  selectorKey?: string
  srOnlyLabel?: string
  textColor?: string | null
  id?: string
  hidePointerCursor?: boolean
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

const checkExternalLink = (href?: string) => {
  if (!href) return true
  try {
    const url = new URL(href, window.location.origin)
    return url.origin !== window.location.origin
  } catch (e) {
    return true
  }
}

const LinkWithOutTracking = forwardRef<LinkProps, any>(function LinkWithOutTrackingEl(
  {
    className,
    onClick,
    href,
    children,
    target,
    disabled,
    withoutBaseStyle = false,
    enableEarlyFetch = false,
    style,
    selectorKey,
    srOnlyLabel,
    textColor,
    id = '',
    hidePointerCursor = false,
    onMouseEnter,
    onMouseLeave,
  },
  ref: React.ForwardedRef<any>,
) {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const styleWithTextColor = textColor?.match(HEX_COLOR_CODE_REGEX)
    ? { ...style, color: textColor, backgroundImage: `linear-gradient(${textColor}, ${textColor})` }
    : style
  const [isExternalLink, setIsExternalLink] = useState(checkExternalLink.bind(null, href))

  const finalHref = useMemo(() => {
    if (checkExternalLink(href)) {
      return href
    }

    const pageExperimentForSlug = PAGE_EXPERIMENTS_CONFIG_LIST.find(experiment => experiment.pages.includes(href))
    if (pageExperimentForSlug) {
      const assignedBucket = getAssignedBucketForPageExperiment(pageExperimentForSlug.name)
      if (assignedBucket) {
        return getSlugForBucket(pageExperimentForSlug, assignedBucket, href)
      }
    }

    return href
  }, [href])

  useEffect(() => {
    setIsExternalLink(checkExternalLink(href))
  }, [href])

  /**
   * NOTE: NextLink doesn't play well with hash updates
   *  */
  const checkHashUpdate = () => {
    if (!href) return true
    const targetURL = new URL(href, window.location.origin)
    const targetURLWithoutHash = targetURL.origin + targetURL.pathname + (targetURL.search || '?')
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const currentURLWithoutHash = window.location.origin + pathname + '?' + searchParams?.toString()
    const isSamePath = targetURLWithoutHash === currentURLWithoutHash
    const isSameHash = window.location.hash === targetURL.hash
    return isSamePath && !isSameHash
  }

  return isExternalLink || checkHashUpdate() ? (
    <a
      {...(id ? { id, ['data-testid']: id } : {})}
      ref={ref}
      href={finalHref}
      target={target}
      style={styleWithTextColor}
      className={classNames(className, !hidePointerCursor && 'cursor--pointer', {
        [styles.wrapper ?? '']: !withoutBaseStyle,
        [styles.disabled ?? '']: disabled,
      })}
      onClick={onClick}
      data-selector-key={selectorKey}
    >
      {srOnlyLabel && <span className="sr-only">{srOnlyLabel}</span>}
      {children}
    </a>
  ) : (
    <Link
      {...(id ? { id, ['data-testid']: id } : {})}
      ref={ref}
      href={finalHref}
      target={target}
      style={styleWithTextColor}
      className={classNames(className, !hidePointerCursor && 'cursor--pointer', {
        [styles.wrapper ?? '']: !withoutBaseStyle,
        [styles.disabled ?? '']: disabled,
      })}
      onClick={onClick}
      aria-disabled={disabled ? 'true' : null}
      tabIndex={disabled ? -1 : undefined}
      data-selector-key={selectorKey}
      prefetch={enableEarlyFetch}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {srOnlyLabel && <span className="sr-only">{srOnlyLabel}</span>}
      {children}
    </Link>
  )
})

export { LinkWithOutTracking }

export default memo(withTracking(LinkWithOutTracking, true))
